import { useState } from "react";
import { useNavigate } from "react-router-dom";
import FlightSearchBar from "../flights/FlightSearchbar";
import HotelSearchbar from "../hotels/HotelSearchbar";
import FareCalendar from "../flights/FareCalendar";
import { useSearch } from "../../context/SearchContext";
import styles from "../../styles";
import { homeNonFlightTabsEnabled } from "../../utils/features";
import { FaCalendarAlt } from "react-icons/fa";

const Hero = () => {
  const [showDirectOnly, setShowDirectOnly] = useState(false);
  const [activeTab, setActiveTab] = useState<"flights" | "hotels">("flights");
  const [showCalendar, setShowCalendar] = useState(false);
  const navigate = useNavigate();
  const { origin, destination, searchParams } = useSearch();
  const canShowCalendar = Boolean(origin && destination && origin !== destination);

  return (
    <section className="bg-white py-3 margin-x-auto max-w-screen-xl mx-auto px-4 relative">
      {/* Banner with only video */}
      <div className={`${styles.boxWidth} mb-6 sm:mb-8`}>
        <div className="relative w-full aspect-[16/6] xs:aspect-[16/5] sm:aspect-[16/4] rounded-lg overflow-hidden">
          <video
            src="/assets/illustration.mp4"
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            className="absolute inset-0 w-full h-full object-cover"
          />
        </div>
      </div>

      {/* Navigation Tabs */}
      <nav
        role="tablist"
        aria-label="Primary navigation"
        className={`${styles.boxWidth} ${styles.marginX} mx-auto flex gap-4 sm:gap-6 md:gap-6 lg:gap-8 justify-start md:justify-center text-heading font-sora text-base font-semibold mb-4 overflow-x-auto py-2`}
      >
        <button
          role="tab"
          aria-selected={activeTab === "flights"}
          aria-pressed={activeTab === "flights"}
          className={`flex-shrink-0 ${styles.btnMin} w-[92px] sm:w-[104px] h-[48px] sm:h-[55px] px-4 py-1.5 rounded-xl transition-colors focus:outline-none focus:ring-2 focus:ring-primary ${
            activeTab === "flights"
              ? "bg-primary text-white"
              : "text-heading hover:text-primary"
          }`}
          onClick={() => setActiveTab("flights")}
        >
          Flights
        </button>
        {/* Everything but Flights. Hidden for phase 1, which sells flights
            only — see homeNonFlightTabsEnabled. Left whole rather than
            deleted: the hotel search bar below and the visa route still
            exist, so this comes back by setting one variable. */}
        {homeNonFlightTabsEnabled && (
          <>
            <button
              role="tab"
              aria-selected={activeTab === "hotels"}
              aria-pressed={activeTab === "hotels"}
              className={`flex-shrink-0 ${styles.btnMin} w-[92px] sm:w-[104px] h-[48px] sm:h-[55px] px-4 py-1.5 rounded-xl transition-colors focus:outline-none focus:ring-2 focus:ring-primary ${
                activeTab === "hotels"
                  ? "bg-primary text-white"
                  : "text-heading hover:text-primary"
              }`}
              onClick={() => setActiveTab("hotels")}
            >
              Hotels
            </button>
            <button
              role="tab"
              className="flex-shrink-0 h-[48px] sm:h-[55px] px-3 rounded-xl text-heading hover:text-primary transition focus:outline-none focus:ring-2 focus:ring-primary"
              onClick={() => navigate("/visa-assistance")}
            >
              Visas
            </button>
            <button className="flex-shrink-0 h-[48px] sm:h-[55px] px-3 rounded-xl text-heading hover:text-primary transition">
              Holidays
            </button>
            <button className="flex-shrink-0 h-[48px] sm:h-[55px] px-3 rounded-xl text-heading hover:text-primary transition">
              Offers
            </button>
            <button className="flex-shrink-0 h-[48px] sm:h-[55px] px-3 rounded-xl text-heading hover:text-primary transition">
              More
            </button>
          </>
        )}
      </nav>

      {/* Search Bar Section */}
      <div className="max-w-screen-xl mx-auto">
        {activeTab === "flights" ? (
          <FlightSearchBar
            showDirectOnly={showDirectOnly}
            setShowDirectOnly={setShowDirectOnly}
          />
        ) : (
          <HotelSearchbar />
        )}
      </div>

      {/* Fare Calendar — shows once user has picked origin + destination */}
      {activeTab === "flights" && canShowCalendar && (
        <div className="max-w-screen-xl mx-auto mt-5">
          <button
            onClick={() => setShowCalendar((p) => !p)}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border transition ${
              showCalendar
                ? "bg-primary text-white border-primary"
                : "bg-white text-gray-700 border-gray-200 hover:border-primary hover:text-primary"
            }`}
          >
            <FaCalendarAlt size={12} />
            {showCalendar ? "Hide price calendar" : "See prices across the month"}
          </button>
          {showCalendar && (
            <div className="mt-3">
              <FareCalendar
                origin={String(origin).toUpperCase()}
                destination={String(destination).toUpperCase()}
                initialDate={searchParams.departureDate || undefined}
                tripType={searchParams.returnDate ? "roundtrip" : "oneway"}
                lengthOfStay={
                  searchParams.returnDate && searchParams.departureDate
                    ? Math.max(
                        1,
                        Math.round(
                          (new Date(searchParams.returnDate).getTime() -
                            new Date(searchParams.departureDate).getTime()) /
                            86400000
                        )
                      )
                    : undefined
                }
              />
            </div>
          )}
        </div>
      )}
    </section>
  );
};

export default Hero;
